import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import styled from 'styled-components';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import Loading from '../../../../../shared/loading';
import defaultOnErrorHandler from '../../../../../shared/default-on-error-handler';
import { SectionDetailLabel, selectorProps } from '../styles';
import { usePostUpdateLocation } from '../../../../../services/profiles/contributor-settings-service';
import { getAllCountries, getAllCitiesByCountry } from '../../../../../services/location-service';

const Container = styled.div`
  width: 100%;
  max-width: ${props => (props.theme.isXLarge ? '330px' : 'none')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  & > .scity-container {
    margin-top: 16px;
  }

  & > *[class$="-container"] {
    width: 100%;

    div:not([class$="-menu"]) {
      width: unset !important;
      margin-left: unset !important;
    }
  }
`;

const mapper = c => ({ label: c.name, value: c.id });

export default function CurrentLocation({ country, city, onUpdate }) {
  const [cn, setCn] = React.useState(null);
  const [cnList, setCnList] = React.useState([]);
  const [ct, setCt] = React.useState(null);
  const [ctList, setCtList] = React.useState([]);
  const [citiesLoaded, setCitiesLoaded] = React.useState(false);
  const [changed, setChanged] = React.useState(false);
  const [{ loading, error }, post] = usePostUpdateLocation();
  const loadingRef = React.useRef();

  useChangeAwareEffect(({ isMount }) => {
    isMount && getAllCountries().then(({ data }) => {
      setCitiesLoaded(false);
      const countries = _.get(data, 'countries', []);
      setCnList(countries.map(mapper));
      if (country) {
        const found = _.find(countries, { slug: country });
        setCn(found ? { label: found.name, value: found.id } : null);
      }
    });
  }, { country });

  useChangeAwareEffect(({ did }) => {
    if (did.cn.notChange || !cn || cn.value === undefined) {
      return;
    }

    setCt(null);
    setCitiesLoaded(false);
    getAllCitiesByCountry(cn.value).then(({ data }) => {
      const cities = _.get(data, 'cities', []);
      setCtList(cities.map(mapper));
      if (city) {
        const found = _.find(cities, { slug: city });
        setCt(found ? { label: found.name, value: found.id } : null);
      }
      setCitiesLoaded(true);
    });
  }, { cn, city });

  React.useEffect(() => (loadingRef.current[loading ? 'start' : 'stop']()), [loading]);

  React.useEffect(() => {
    error && defaultOnErrorHandler(error, 'Error saving location');
  }, [error]);

  useChangeAwareEffect(({ did }) => {
    const validChange = (did.cn.change || did.ct.change || did.citiesLoaded.change)
      && changed && cn && citiesLoaded && (ct || !ctList.length);
    validChange && post({
      country: cn.value,
      city: ctList.length ? _.get(ct, 'value', null) : null,
    }).then(() => onUpdate({ country: cn.label }));
  }, {
    cn, ct, citiesLoaded, changed, ctList,
  });

  const changeCountry = v => {
    setChanged(true);
    setCitiesLoaded(false);
    setCn(v);
    setCtList([]);
  };

  const changeCity = v => {
    setChanged(true);
    setCt(v);
  };

  return (
    <article>
      <SectionDetailLabel>where are you currently based:</SectionDetailLabel>
      <Container>
        <Select
          {...selectorProps}
          value={cn}
          className="scountry-container"
          placeholder="Country / Region"
          options={cnList}
          onChange={changeCountry}
        />
        {!!ctList.length && (
          <Select
            {...selectorProps}
            value={ct}
            className="scity-container"
            placeholder="City"
            options={ctList}
            onChange={changeCity}
          />
        )}
      </Container>
      <Loading variant="sip" ref={loadingRef} />
    </article>
  );
}

CurrentLocation.propTypes = {
  country: PropTypes.string,
  city: PropTypes.string,
  onUpdate: PropTypes.func,
};

CurrentLocation.defaultProps = {
  country: null,
  city: null,
  onUpdate: () => null,
};
